import {
  Box, Card, CardContent, Chip, Grid, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useState } from 'react';
import HealingIcon from '@mui/icons-material/Healing';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MedicationIcon from '@mui/icons-material/Medication';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { EmptyState } from '@/components/EmptyState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { LongitudinalRecord } from '@/types/medical';

/**
 * My record.
 *
 * Read-only view of the FHIR-like longitudinal bundle returned by
 * `services/access.build_patient_bundle`. The patient owns this view.
 *
 * We split into tabs because the bundle contains 6 different concept
 * categories. Each tab is a thin renderer; the bundle is one HTTP call.
 */

type TabKey = 'conditions' | 'allergies' | 'medications' | 'immunizations' | 'observations';

const TABS: { key: TabKey; label: string; icon: React.ReactNode; }[] = [
  { key: 'conditions',    label: 'Conditions',    icon: <HealingIcon /> },
  { key: 'allergies',     label: 'Allergies',     icon: <WarningAmberIcon /> },
  { key: 'medications',   label: 'Medications',   icon: <MedicationIcon /> },
  { key: 'immunizations', label: 'Immunizations', icon: <VaccinesIcon /> },
  { key: 'observations',  label: 'Observations',  icon: <MonitorHeartIcon /> },
];

export function PatientRecordPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'record'],
    queryFn:  patient.record.get,
  });
  const [tab, setTab] = useState<TabKey>('conditions');

  return (
    <>
      <PageHeader
        title="My record"
        subtitle="Conditions, allergies, medications, immunizations and recent observations."
      />

      <DataState query={q} loadingLabel="Decrypting your record…">
        {(record) => (
          <>
            <SummaryStrip record={record} />
            <Tabs
              value={tab}
              onChange={(_, v: TabKey) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              {TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  icon={t.icon as React.ReactElement}
                  iconPosition="start"
                  label={`${t.label} (${(record[t.key] ?? []).length})`}
                />
              ))}
            </Tabs>
            <TabContent record={record} tab={tab} />
          </>
        )}
      </DataState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SummaryStrip({ record }: { record: LongitudinalRecord }) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {TABS.map((t) => (
        <Grid item xs={6} sm={4} md={2.4} key={t.key}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ color: 'primary.main', display: 'flex' }}>{t.icon}</Box>
                <Box>
                  <Typography variant="overline" color="text.secondary"
                              sx={{ display: 'block', lineHeight: 1.2 }}>
                    {t.label}
                  </Typography>
                  <Typography variant="h6" className="tabular">
                    {(record[t.key] ?? []).length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function TabContent({ record, tab }: { record: LongitudinalRecord; tab: TabKey }) {
  if (tab === 'conditions')    return <ConditionsList items={record.conditions} />;
  if (tab === 'allergies')     return <AllergiesList  items={record.allergies} />;
  if (tab === 'medications')   return <MedicationsList items={record.medications} />;
  if (tab === 'immunizations') return <ImmunizationsList items={record.immunizations} />;
  return <ObservationsList items={record.observations} />;
}

function ConditionsList({ items }: { items: LongitudinalRecord['conditions'] }) {
  if (items.length === 0)
    return <EmptyState title="No conditions on record" description="Your clinician adds conditions during a consultation." />;
  return (
    <Stack spacing={1.5}>
      {items.map((c) => (
        <Card key={c.id}>
          <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>{c.display}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.code_system?.toUpperCase()} {c.code}
                  {c.onset_date && ` · onset ${formatDate(c.onset_date)}`}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={c.clinical_status}
                color={c.clinical_status === 'active' ? 'warning' : 'default'}
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function AllergiesList({ items }: { items: LongitudinalRecord['allergies'] }) {
  if (items.length === 0)
    return <EmptyState title="No allergies on record" description="Tell your clinician about any reactions you've had." />;
  return (
    <Stack spacing={1.5}>
      {items.map((a) => (
        <Card key={a.id}>
          <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>{a.substance_display}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {a.category} {a.reaction && `· ${a.reaction}`}
                </Typography>
              </Box>
              <SeverityBadge severity={a.severity} />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function MedicationsList({ items }: { items: LongitudinalRecord['medications'] }) {
  if (items.length === 0)
    return <EmptyState title="No active medications" description="Active medications are pulled from your prescriptions and what you tell your clinician." />;
  return (
    <Stack spacing={1.5}>
      {items.map((m) => (
        <Card key={m.id}>
          <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>{m.medication_display}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.dose_text}
                  {m.started_on && ` · since ${formatDate(m.started_on)}`}
                </Typography>
              </Box>
              {m.is_self_reported && (
                <Chip size="small" label="self-reported" variant="outlined" />
              )}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function ImmunizationsList({ items }: { items: LongitudinalRecord['immunizations'] }) {
  if (items.length === 0)
    return <EmptyState title="No immunizations on record" />;
  return (
    <Stack spacing={1.5}>
      {items.map((v) => (
        <Card key={v.id}>
          <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>{v.vaccine_display}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(v.administered_on)}
                  {v.dose_number != null && ` · dose ${v.dose_number}`}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function ObservationsList({ items }: { items: LongitudinalRecord['observations'] }) {
  if (items.length === 0)
    return <EmptyState title="No observations recorded" description="Vitals and lab results show up here as your clinician records them or your devices report them." />;
  return (
    <Stack spacing={1.5}>
      {items.map((o) => (
        <Card key={o.id}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={500} noWrap>{o.display}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.code_system?.toUpperCase()} {o.code} · {o.source}
                </Typography>
              </Box>
              <Stack alignItems="flex-end">
                <Typography variant="body1" className="tabular" fontWeight={600}>
                  {o.value_quantity ?? o.value_text}
                  {o.value_unit && <Typography component="span" variant="caption" color="text.secondary">
                    {' '}{o.value_unit}
                  </Typography>}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(o.measured_at)}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const sev = (severity || '').toLowerCase();
  const color = ['severe', 'fatal'].includes(sev) ? 'error'
              : sev === 'moderate' ? 'warning'
              : 'default';
  return (
    <Chip
      size="small"
      label={severity || 'unknown'}
      color={color as 'error' | 'warning' | 'default'}
      variant="outlined"
      sx={{ textTransform: 'capitalize' }}
    />
  );
}
